import type { KeyboardEvent } from 'react'
import { useCallback, useRef } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'

import { useEscapeStack } from '#/shared/lib/hooks'
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from '#/shared/ui/icons'

export interface LightboxImage {
  src: string
  /** Accessible name for the image. */
  alt: string
  /** Optional caption shown under the image (e.g. file name or sender line). */
  caption?: string
}

interface ImageLightboxProps {
  /** Images in the active gallery. */
  images: ReadonlyArray<LightboxImage>
  /** Index of the shown image; null closes the viewer. */
  index: number | null
  /** Navigate to another image (arrows / arrow keys wrap around the set). */
  onIndexChange: (index: number) => void
  onClose: () => void
  closeLabel: string
  prevLabel: string
  nextLabel: string
  /** Optional "2 / 10" style counter; only shown when there are 2+ images. */
  counter?: string
}

// Dark, blurred backdrop so the white text stays readable when it overlaps a
// light image (white-on-white with bg-white/10).
const PILL_CLASS =
  'rounded-full bg-black/45 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-md'

const CONTROL_CLASS =
  'flex size-10 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus:outline-none'

const ARROW_DELTA: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1 }

/** The centered image plus its navigation arrows, counter, and caption. */
function LightboxView({
  current,
  multiple,
  counter,
  prevLabel,
  nextLabel,
  closeLabel,
  onPrev,
  onNext,
}: {
  current: LightboxImage | null
  multiple: boolean
  counter?: string
  prevLabel: string
  nextLabel: string
  closeLabel: string
  onPrev: () => void
  onNext: () => void
}) {
  return (
    <>
      <DialogPrimitive.Title className="sr-only">
        {current?.alt ?? ''}
      </DialogPrimitive.Title>
      {current && (
        <img
          src={current.src}
          alt={current.alt}
          className="max-h-full max-w-full rounded-lg object-contain"
        />
      )}
      {multiple && (
        <>
          <button
            type="button"
            aria-label={prevLabel}
            onClick={onPrev}
            className={`${CONTROL_CLASS} absolute top-1/2 left-4 -translate-y-1/2`}
          >
            <ChevronLeftIcon className="size-5" />
          </button>
          <button
            type="button"
            aria-label={nextLabel}
            onClick={onNext}
            className={`${CONTROL_CLASS} absolute top-1/2 right-4 -translate-y-1/2`}
          >
            <ChevronRightIcon className="size-5" />
          </button>
        </>
      )}
      {multiple && counter ? (
        <span className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2">
          <span className={PILL_CLASS}>{counter}</span>
        </span>
      ) : null}
      {current?.caption ? (
        <span className="pointer-events-none absolute inset-x-0 bottom-8 flex justify-center px-6">
          <span className={`max-w-full truncate ${PILL_CLASS}`}>
            {current.caption}
          </span>
        </span>
      ) : null}
      <DialogPrimitive.Close
        aria-label={closeLabel}
        className={`${CONTROL_CLASS} absolute top-4 right-4`}
      >
        <XIcon className="size-5" />
      </DialogPrimitive.Close>
    </>
  )
}

/**
 * Full-screen image viewer: dims the page to black and centers the image. When
 * given multiple images it becomes a gallery — left/right arrows and the arrow
 * keys move through the set (wrapping at the ends).
 */
export function ImageLightbox({
  images,
  index,
  onIndexChange,
  onClose,
  closeLabel,
  prevLabel,
  nextLabel,
  counter,
}: ImageLightboxProps) {
  const open = index !== null && index < images.length
  const current = open ? images[index] : null

  // Take the top of the shared escape stack while open so Escape closes only the
  // viewer — not whatever opened it (e.g. the conversation thread underneath).
  // A stable callback keeps the registration steady across renders.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const closeFromEscape = useCallback(() => onCloseRef.current(), [])
  useEscapeStack(open, closeFromEscape)

  const go = useCallback(
    (delta: number) => {
      if (index === null || images.length === 0) return
      onIndexChange((index + delta + images.length) % images.length)
    },
    [index, images.length, onIndexChange],
  )

  const onKeyDown = (event: KeyboardEvent) => {
    const delta = ARROW_DELTA[event.key]
    if (images.length < 2 || !delta) return
    event.preventDefault()
    go(delta)
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="app-frame-inset fixed z-70 bg-black/90 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out data-[state=open]:fade-in" />
        <DialogPrimitive.Content
          onKeyDown={onKeyDown}
          className="app-frame-inset fixed z-70 flex items-center justify-center p-6 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95"
        >
          <LightboxView
            current={current}
            multiple={images.length > 1}
            counter={counter}
            prevLabel={prevLabel}
            nextLabel={nextLabel}
            closeLabel={closeLabel}
            onPrev={() => go(-1)}
            onNext={() => go(1)}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
